// Chemin: C:\gestion-copro\src\components\layout\CoproprietaireLayout.tsx
// Layout pour l'espace copropriétaire
// Session 56 : création initiale
// Session 74 : Ajout bouton "Espace Conseil Syndical" pour membres CS
// Header vert sapin (#166534) pour différenciation visuelle

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  AlertTriangle,
  PlusCircle,
  FileText,
  LogOut,
  Menu,
  X,
  User,
  Building2,
  ChevronDown,
  Shield
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';

interface CoproprietaireLayoutProps {
  children: React.ReactNode;
}

const CoproprietaireLayout: React.FC<CoproprietaireLayoutProps> = ({ children }) => {
  const { firebaseUser, userProfile, signOut } = useAuth();
  const { copropriete } = useCopropriete();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Vérifier si l'utilisateur est membre du CS (peut accéder à l'espace admin)
  const isMembreCS = userProfile?.role === 'cs';

  // Navigation copropriétaire (simplifiée)
  const navigation = [
    {
      name: 'Tableau de bord',
      href: '/espace-copro',
      icon: Home,
      exact: true
    },
    {
      name: 'Mes signalements',
      href: '/espace-copro/mes-signalements',
      icon: AlertTriangle
    },
    {
      name: 'Nouveau signalement',
      href: '/espace-copro/signaler',
      icon: PlusCircle
    },
    {
      name: 'Documents',
      href: '/espace-copro/documents',
      icon: FileText
    }
  ];

  const isActive = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header vert sapin */}
      <header className="bg-green-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo et nom copropriété */}
            <div className="flex items-center gap-4">
              {/* Bouton menu mobile */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-green-100 hover:bg-green-700 transition-colors"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>

              {/* Logo */}
              <Link to="/espace-copro" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-green-800" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-white font-bold text-lg leading-tight">
                    {copropriete?.nom || 'Mon espace'}
                  </h1>
                  <p className="text-green-200 text-xs">Espace copropriétaire</p>
                </div>
              </Link>
            </div>

            {/* Navigation desktop */}
            <nav className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href, item.exact);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? 'bg-green-700 text-white'
                        : 'text-green-100 hover:bg-green-700/50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Zone droite : Bouton CS + Menu utilisateur */}
            <div className="flex items-center gap-3">
              {/* Bouton Espace Conseil Syndical (visible pour membres CS) */}
              {isMembreCS && (
                <Link
                  to="/admin"
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  Espace CS
                </Link>
              )}

              {/* Menu utilisateur */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-green-100 hover:bg-green-700 transition-colors"
                >
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-white">
                      {userProfile?.prenom || 'Mon compte'}
                    </p>
                    <p className="text-xs text-green-200">
                      {isMembreCS ? 'Membre CS' : 'Copropriétaire'}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-green-200 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {userProfile?.prenom} {userProfile?.nom}
                        </p>
                        <p className="text-xs text-gray-500">{firebaseUser?.email}</p>
                      </div>
                      
                      <div className="px-4 py-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isMembreCS 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {isMembreCS ? 'Membre du Conseil Syndical' : 'Copropriétaire'}
                        </span>
                      </div>

                      <div className="border-t border-gray-100 mt-2 pt-2 px-2">
                        {/* Lien Espace CS (visible dans dropdown pour mobile) */}
                        {isMembreCS && (
                          <Link
                            to="/admin"
                            onClick={() => setUserMenuOpen(false)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Shield className="w-4 h-4" />
                            Espace Conseil Syndical
                          </Link>
                        )}

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Se déconnecter
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Menu mobile */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-green-700">
            <nav className="px-4 py-3 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href, item.exact);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? 'bg-green-700 text-white'
                        : 'text-green-100 hover:bg-green-700/50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              })}

              {/* Lien Espace CS dans menu mobile */}
              {isMembreCS && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  <Shield className="w-5 h-5" />
                  Espace Conseil Syndical
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer simple */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-gray-500">
            Copro Protect © {new Date().getFullYear()} — Espace copropriétaire
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CoproprietaireLayout;
